<script lang="ts" setup>
import { PropType, ref, computed, nextTick, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { Modal, UserChip } from "@/components";
import {
  DocumentSpaceAccess,
  useTransferOwnershipMutation,
} from "@/graphql/operations";
import AccessDropdown from "./AccessDropdown.vue";

const { t } = useI18n();
const initialFocus = ref<HTMLButtonElement>();

const emits = defineEmits(["close", "refreshData", "visibilityError"]);

const props = defineProps({
  newOwner: {
    type: Object as PropType<DocumentSpaceAccess>,
    required: true,
  },
  visibleUserCount: {
    type: Number,
    required: true,
  },
  visibleUserLimit: {
    type: Number,
    required: true,
  },
});

const defaultVisibility = computed(() =>
  props.visibleUserCount < props.visibleUserLimit ||
  props.newOwner.displayContactDetails
    ? "visible"
    : "hidden",
);

const items = computed(() => {
  const i = [];
  i.push({
    value: "hidden",
    title: t("documentSpace.access.hideDetails.title"),
    info: "Contact details won't be displayed in the shared space",
    displayCurrentSelection: true,
  });

  if (
    props.visibleUserCount < props.visibleUserLimit ||
    props.newOwner.displayContactDetails
  ) {
    i.push({
      value: "visible",
      title: t("documentSpace.access.displayDetails.title"),
      info: t("documentSpace.access.displayDetails.info"),
      displayCurrentSelection: true,
    });
  } else {
    i.push({
      value: "visible",
      title: t("documentSpace.access.displayDetails.title"),
      info: t("documentSpace.access.displayDetails.disabled.info"),
      disabled: true,
      displayCurrentSelection: true,
    });
  }

  return i;
});

const visibility = ref<string>(defaultVisibility.value);
const setVisibility = (v: string) => {
  visibility.value = v;
};

const transferError = ref(false);
const { executeMutation: executeTransferOwnership, fetching: transferring } =
  useTransferOwnershipMutation();

const submit = async () => {
  if (transferring.value) return;
  transferError.value = false;
  const result = await executeTransferOwnership({
    accessId: props.newOwner.id,
    displayContactDetails: visibility.value === "visible",
  });

  if (!result.data?.transferOwnershipOfSpace) {
    transferError.value = true;
    return;
  }

  // The transfer root already committed: the former owner cannot repeat it.
  // Refresh the actual roles and let the access dialog report the separate
  // visibility failure instead of presenting an invalid transfer retry.
  if (!result.data?.updateDocumentSpaceAccess) emits("visibilityError");
  emits("refreshData");
  emits("close");
};

onMounted(() => {
  void nextTick(() => initialFocus.value?.focus());
});
</script>

<template>
  <modal :active="true" :close-disabled="transferring" @close="emits('close')">
    <template #header>
      <button
        type="button"
        ref="initialFocus"
        :disabled="transferring"
        class="icon_button material-symbols-outlined left"
        :aria-label="$t('back')"
        @click="emits('close')"
      >
        keyboard_backspace
      </button>
      {{ $t("documentSpace.access.transferOwnership.title") }}
    </template>

    <template #body>
      <p v-if="transferError" role="alert" class="save_error">
        {{ $t("error") }}
      </p>
      <div class="inner">
        <div class="flex_container" :inert="transferring">
          <user-chip
            :displayName="newOwner.user!.displayName!"
            :email="newOwner.user!.email!"
            :profileImgUrl="
              newOwner.user!.userContactDetail?.profileImageUrl || undefined
            "
          />
          <access-dropdown
            class="settings"
            :items="items"
            :value="visibility"
            @change="setVisibility"
          />
        </div>
        <div class="message info">
          {{ $t("documentSpace.access.transferOwnership.info") }}
        </div>
      </div>
    </template>

    <template #footer>
      <div class="buttons">
        <button
          type="button"
          class="button primary"
          :disabled="transferring"
          @click="submit"
        >
          {{ $t("documentSpace.access.transferOwnership.submit") }}
        </button>
      </div>
    </template>
  </modal>
</template>

<style scoped lang="scss">
.save_error {
  padding: 0.75em;
  color: var(--classic-error-text, #9f3a38);
  background: var(--classic-error-surface, #fff6f6);
}

.info {
  margin-top: 1em;
}

.material-symbols-outlined.left {
  margin-right: 0.3em;
}

.icon_button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
}

.icon_button:hover {
  cursor: pointer;
}
</style>
